// Pages/Ess/leaveUtils.js
// Date / duration helpers for leave pages. The formatting output matches the
// previous MyLeave.jsx; the only change is that "YYYY-MM-DD" strings are
// parsed as LOCAL dates (new Date("2026-01-18") is UTC midnight and can show
// the previous day in timezones behind UTC).

const DAY_MS = 24 * 60 * 60 * 1000;

export const parseLocalDate = (value) => {
  // new Date(null) is 1 Jan 1970 and new Date("") is invalid - treat both as "no date".
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const sortedLeaveDates = (dates) =>
  (dates || [])
    .map(parseLocalDate)
    .filter(Boolean)
    .sort((a, b) => a - b);

const fmt = (date, withYear) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
  });

export const formatDates = (dates) => {
  const sorted = sortedLeaveDates(dates);
  if (sorted.length === 0) return "N/A";
  if (sorted.length === 1) return fmt(sorted[0], true);

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const consecutive = sorted.every(
    (date, index) =>
      index === 0 || Math.round((date - sorted[index - 1]) / DAY_MS) === 1,
  );

  if (consecutive) return `${fmt(first, false)} - ${fmt(last, true)}`;
  return `${sorted.length} dates (${fmt(first, false)} - ${fmt(last, true)})`;
};

export const formatDateLong = (date) =>
  date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export const formatDuration = (duration) => {
  const durationMap = {
    FIRST_HALF: "First Half",
    SECOND_HALF: "Second Half",
    FULL_DAY: "Full Day",
  };
  return durationMap[duration] || duration || "N/A";
};

// Timestamp of the earliest leave date (used for sorting), or null.
export const firstLeaveTimestamp = (dates) => {
  const sorted = sortedLeaveDates(dates);
  return sorted.length ? sorted[0].getTime() : null;
};

// Approved leave can be revoked up to and including its last day.
export const canRevokeLeave = (leaveDates) => {
  const sorted = sortedLeaveDates(leaveDates);
  if (sorted.length === 0) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today <= sorted[sorted.length - 1];
};

// True when any of the leave's dates falls inside [from, to] (inclusive).
// `from` / `to` are "YYYY-MM-DD" strings from <input type="date"> or "".
export const leaveInDateRange = (leaveDates, from, to) => {
  if (!from && !to) return true;
  const start = from ? parseLocalDate(from) : null;
  const end = to ? parseLocalDate(to) : null;
  return sortedLeaveDates(leaveDates).some(
    (date) => (!start || date >= start) && (!end || date <= end),
  );
};

// ---------------------------------------------------------------------------
// Local-date helpers for <input type="date"> values ("YYYY-MM-DD")
// ---------------------------------------------------------------------------

// Local "YYYY-MM-DD" (NOT toISOString(), which converts to UTC and can land on
// the previous / next day depending on the timezone).
export const toInputValue = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const todayInputValue = () => toInputValue(new Date());

// "Jan 18, 2026" or "N/A"
export const formatDateShort = (value) => {
  const date = parseLocalDate(value);
  return date ? fmt(date, true) : "N/A";
};

// "18/01/2026" (the format Apply Leave has always used) or ""
export const formatDateGB = (value) => {
  const date = parseLocalDate(value);
  return date
    ? date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";
};

// Whole days from `from` to `to`, counting both ends (same day -> 1).
export const daysInclusive = (from, to) => {
  const start = parseLocalDate(from);
  const end = parseLocalDate(to);
  if (!start || !end || end < start) return 0;
  return Math.round((end - start) / DAY_MS) + 1;
};
